import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Settings } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { useAuth } from '../../../contexts/AuthContext';

// Tab components
import ProfileTab from './components/ProfileTab';
import NotificationsTab from './components/NotificationsTab';
import JobPreferencesTab from './components/JobPreferencesTab';
import SecurityAccountTab from './components/SecurityAccountTab';

/**
 * Contractor Settings Page
 * Personal settings interface with 4 tabs:
 * 1. Profile - Avatar, personal info, professional info, display preferences
 * 2. Notifications - Job alerts, earnings notifications, digest preferences
 * 3. Job Preferences - Availability, job types, skills, location preferences
 * 4. Security & Account - Password (OTP), sessions, data export
 *
 * Navigation is now handled via the main Sidebar with expandable sub-items.
 */
const ContractorSettings = () => {
  const [searchParams] = useSearchParams();
  const { loading: authLoading } = useAuth();

  // Get tab from URL or default to 'profile'
  const activeTab = searchParams.get('tab') || 'profile';

  // Tab titles for the header
  const tabTitles = {
    profile: 'Profile',
    notifications: 'Notifications',
    preferences: 'Job Preferences',
    security: 'Security & Account'
  };

  const tabDescriptions = {
    profile: 'Manage your personal and professional information',
    notifications: 'Configure your job alerts and notification preferences',
    preferences: 'Set your availability, skills, and job preferences',
    security: 'Update your password and manage your account'
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab />;
      case 'notifications':
        return <NotificationsTab />;
      case 'preferences':
        return <JobPreferencesTab />;
      case 'security':
        return <SecurityAccountTab />;
      default:
        return <ProfileTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />

      <main className="ml-0 lg:ml-60 mt-[73px] min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shrink-0">
                <Settings size={20} color="white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-text-primary">{tabTitles[activeTab] || 'Settings'}</h1>
                <p className="text-text-secondary text-sm">
                  {tabDescriptions[activeTab] || 'Manage your profile and preferences'}
                </p>
              </div>
            </div>
          </div>

          {/* Settings Content */}
          <div className="bg-surface border border-border rounded-xl p-4 sm:p-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContractorSettings;
